import React, { createContext, useContext, useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
const CheckCircleIcon = () => (
  <svg viewBox="0 0 512 512" width="1em" height="1em" fill="currentColor">
    <path d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628 0z" />
  </svg>
);

const ExclamationTriangleIcon = () => (
  <svg viewBox="0 0 576 512" width="1em" height="1em" fill="currentColor">
    <path d="M569.517 440.013L327.525 21.026C319.467 6.953 304.532 0 288 0s-31.467 6.953-39.525 21.026L6.483 440.013C-1.575 454.086-2.158 471.216 5.006 485.807 12.17 500.398 25.992 512 42.008 512h491.984c16.016 0 29.838-11.602 37.002-26.193 7.164-14.591 6.581-31.721-1.477-45.794zM288 448c-17.673 0-32-14.327-32-32s14.327-32 32-32 32 14.327 32 32-14.327 32-32 32zm32-112c0 8.837-7.163 16-16 16h-32c-8.837 0-16-7.163-16-16V176c0-8.837 7.163-16 16-16h32c8.837 0 16 7.163 16 16v160z" />
  </svg>
);

const ExclamationCircleIcon = () => (
  <svg viewBox="0 0 512 512" width="1em" height="1em" fill="currentColor">
    <path d="M256 8C119.043 8 8 119.083 8 256c0 136.997 111.043 248 248 248s248-111.003 248-248C504 119.083 392.957 8 256 8zm0 110c23.196 0 42 18.804 42 42s-18.804 42-42 42-42-18.804-42-42 18.804-42 42-42zm56 254c0 6.627-5.373 12-12 12h-88c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h12v-64h-12c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h64c6.627 0 12 5.373 12 12v100h12c6.627 0 12 5.373 12 12v24z" />
  </svg>
);

const TimesIcon = () => (
  <svg viewBox="0 0 352 512" width="1em" height="1em" fill="currentColor">
    <path d="M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.19 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.19 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z" />
  </svg>
);

const ToastContext = createContext();

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const progress = keyframes`
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
`;

const ToastContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Toast = styled.div`
  position: relative;
  min-width: 300px;
  padding: 15px 25px;
  background: ${props => {
    if (props.type === 'error') return '#ff4444';
    if (props.type === 'warning') return '#ffa500';
    return '#4CAF50';
  }};
  color: white;
  border-radius: 8px;
  margin-bottom: 10px;
  cursor: pointer;
  overflow: hidden;
  
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  animation: ${slideIn} 0.3s ease forwards;
  
  &.exit {
    animation: ${slideOut} 0.3s ease forwards;
  }
`;

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.7);
  transform-origin: left;
  
  animation: ${progress} ${props => props.$duration}ms linear forwards;
  animation-play-state: ${props => props.$isPaused ? 'paused' : 'running'};
`;

const ToastContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const IconWrapper = styled.div`
  font-size: 18px;
  display: flex;
  align-items: center;
`;

const ToastMessage = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const ToastTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
`;

const ToastDescription = styled.span`
  font-size: 12px;
  font-weight: 400;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0;
  margin-left: 15px;
  margin-right: -10px;
  opacity: 0.7;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  font-size: 16px;

  &:hover {
    opacity: 1;
  }
`;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const toastTimeouts = useRef(new Map());

  const addToast = (message, type = 'success') => {
    const id = Math.random().toString(36).substr(2, 9);
    const [title, description] = Array.isArray(message) ? message : [message, ''];
    const duration = 4000;

    const newToast = {
      id,
      title,
      description,
      type,
      duration,
      isPaused: false,
      isExiting: false,
      createdAt: Date.now()
    };

    setToasts(prev => [...prev, newToast]);

    const timeout = setTimeout(() => {
      removeToast(id);
    }, duration);

    toastTimeouts.current.set(id, {
      timeoutId: timeout,
      startTime: Date.now()
    });
  };

  const removeToast = (id) => {
    setToasts(prev => 
      prev.map(toast => 
        toast.id === id ? { ...toast, isExiting: true } : toast
      )
    );

    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 300);

    if (toastTimeouts.current.has(id)) {
      clearTimeout(toastTimeouts.current.get(id).timeoutId);
      toastTimeouts.current.delete(id);
    }
  };

  const handleMouseEnter = (id) => {
    setToasts(prev =>
      prev.map(toast =>
        toast.id === id ? { ...toast, isPaused: true } : toast
      )
    );

    if (toastTimeouts.current.has(id)) {
      const { timeoutId, startTime } = toastTimeouts.current.get(id);
      clearTimeout(timeoutId);
      const elapsedTime = Date.now() - startTime;
      toastTimeouts.current.set(id, {
        ...toastTimeouts.current.get(id),
        elapsedTime
      });
    }
  };

  const handleMouseLeave = (id) => {
    setToasts(prev =>
      prev.map(toast =>
        toast.id === id ? { ...toast, isPaused: false } : toast
      )
    );

    if (toastTimeouts.current.has(id)) {
      const { elapsedTime } = toastTimeouts.current.get(id);
      const remainingTime = 4000 - elapsedTime;
      
      const newTimeout = setTimeout(() => {
        removeToast(id);
      }, remainingTime);

      toastTimeouts.current.set(id, {
        timeoutId: newTimeout,
        startTime: Date.now() - elapsedTime
      });
    }
  };

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <ToastContainer>
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            type={toast.type}
            className={toast.isExiting ? 'exit' : ''}
            onMouseEnter={() => handleMouseEnter(toast.id)}
            onMouseLeave={() => handleMouseLeave(toast.id)}
          >
            <ToastContent>
              <IconWrapper>
                {toast.type === 'success' ? <CheckCircleIcon /> : 
                 toast.type === 'warning' ? <ExclamationTriangleIcon /> : 
                 <ExclamationCircleIcon />}
              </IconWrapper>
              <ToastMessage>
                <ToastTitle>{toast.title}</ToastTitle>
                {toast.description && (
                  <ToastDescription>{toast.description}</ToastDescription>
                )}
              </ToastMessage>
            </ToastContent>
            <CloseButton onClick={() => removeToast(toast.id)}>
              <TimesIcon />
            </CloseButton>
            <ProgressBar 
              $duration={toast.duration} 
              $isPaused={toast.isPaused}
            />
          </Toast>
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}; 